import type { QueryClient } from '@tanstack/react-query'

import { prefetchRouteData } from '@/app/query-client'
import { brokersByStockQueryOptions } from '@/features/brokers/api/queries'
import { companyFloorsheetQueryOptions } from '@/features/company/api/queries'
import { SESSIONS_IN_YEAR } from '@/features/market/api/barWindow'
import type { MarketRange } from '@/features/market/api/marketApi'
import { dailyBarsQueryOptions } from '@/features/market/api/queries'
import { priceHistoryQueryOptions } from '@/features/prices/api/queries'
import { metricPresentationsQueryOptions } from '@/features/reference/api/queries'
import type { BrokerPeriod } from '@/schemas/broker'
import { recentSessionsQueryOptions } from '@/screens/stock/lib/sessions'
import type { StockTab } from '@/screens/stock/stockData'

/*
 * The first read each live Stock tab makes, and the defaults that key it.
 *
 * These constants used to be exported from the panels that open on them. They
 * live here now because the `/stocks/$symbol` loader starts the active tab's
 * read from the URL, and a loader must not import a component module: route
 * loaders are not code-split, so the panel — and everything it draws with —
 * would move into the entry chunk. The panels import them from here too, so
 * the loader's read and the panel's first read land on one cache key by
 * construction.
 */

/**
 * The range Overview's chart opens on. `price-history` has its own, below;
 * StockScreen seeds the one period state both tabs share with
 * `initialStockPeriod`, before the first render and so before the first
 * request.
 */
const OVERVIEW_CHART_DEFAULT_RANGE: MarketRange = '1W'

/**
 * The range Price History's table opens on, cold.
 *
 * It has been 1M since the panel was written, and it stayed 1M when the panel
 * kept its own `useState<MarketRange>('1M')`. Lifting that state to StockScreen
 * — so this panel and the sparkline share one `usePriceHistory` key instead of
 * requesting the same series twice — silently inherited StockScreen's own
 * initial value of `'1W'` instead, which changed what this tab shows on arrival.
 * Sharing the key was the intended fix; changing the default was not.
 *
 * It has to be seeded in StockScreen rather than corrected in the panel: the
 * panel pushing a range up on mount would render once at 1W, fire a request
 * for it, then render again at 1M and fire a second — exactly the duplicate
 * request lifting the state removed.
 */
export const PRICE_HISTORY_DEFAULT_RANGE: MarketRange = '1M'

/** The period Broker Holding opens on. */
export const BROKER_HOLDING_DEFAULT_PERIOD: BrokerPeriod = 'today'

/** The page size Floorsheet opens on. */
export const FLOORSHEET_PANEL_PAGE_SIZE = 20

/**
 * How many BARS Technical reads, which is deliberately more than it DRAWS.
 *
 * The frame's chart is six months and rules a 200-day average across it.
 * Those are not the same window: six months of NEPSE sessions is around 120
 * closes, and a 200-day average needs 200. So the screen asks the datafeed for
 * a year of sessions, rules each average from all of it, and draws the last
 * six months. The FIGURES beside the chart are `/technicals/`' own (owner
 * decision 2); only the lines come from these bars.
 *
 * A bar COUNT rather than a range key, because `/api/v5/charts/history/` takes
 * `countback` and not `?range=`. `SESSIONS_IN_YEAR` is derived from mm-be's
 * own `RANGE_DAYS` — see `barWindow.ts`, including what that conversion does
 * not account for.
 */
export const TECHNICAL_COUNTBACK = SESSIONS_IN_YEAR

/** The range the shared price series opens on for a member arriving on `tab`. */
export const initialStockPeriod = (tab: StockTab): MarketRange =>
  tab === 'price-history' ? PRICE_HISTORY_DEFAULT_RANGE : OVERVIEW_CHART_DEFAULT_RANGE

/**
 * Starts the active tab's first read — SECONDARY data for the Stock route, so
 * nothing awaits it: the page renders on the company detail alone, and the
 * panel shows its own skeleton (and its own error) if this is still out.
 *
 * Only the tab the URL opens on. An inactive tab is never prefetched, and a
 * tab switch is a search-param change, which re-runs no loader: the panel
 * then mounts at once (the company is already cached) and reads for itself.
 */
export function prefetchStockTab(client: QueryClient, symbol: string, tab: StockTab): void {
  switch (tab) {
    case 'overview':
    case 'price-history':
      prefetchRouteData(client, priceHistoryQueryOptions(symbol, initialStockPeriod(tab)))
      return
    case 'technical':
      // Not `/technicals/` itself: its factory lives beside every company
      // tab's service and schema (`profileQueries.ts`), and importing it here
      // put 14 KB of them in the entry chunk (+4.3 KB gzipped, measured
      // 2026-10-03) for one round trip on a cold deep link. The panel starts
      // that read when it mounts.
      prefetchRouteData(client, dailyBarsQueryOptions(symbol, TECHNICAL_COUNTBACK))
      prefetchRouteData(client, metricPresentationsQueryOptions())
      prefetchRouteData(client, recentSessionsQueryOptions(symbol))
      return
    case 'floorsheet':
      prefetchRouteData(
        client,
        companyFloorsheetQueryOptions(symbol, 1, FLOORSHEET_PANEL_PAGE_SIZE),
      )
      return
    case 'broker-holding':
      prefetchRouteData(client, brokersByStockQueryOptions(symbol, BROKER_HOLDING_DEFAULT_PERIOD))
      return
    default:
      // The tabs the company API brought start their own first read when
      // their panel mounts; a prefetch for them here is deferred
      // (orchestrator, 2026-10-03), as is one for an Advanced sub-tab, which
      // this switch cannot see.
      return
  }
}
